import { useState, useEffect } from "react";
import { adminApi, syncApi } from "@/services/api";
import { useAuth } from "@/hooks/useAuth";
import { Link, useSearchParams } from "react-router-dom";
import type { User, UserRole, ModuleConfig } from "@/types";
import { MODULE_DEFS, MODULE_LABELS } from "@/utils/modules";
import { ContactPicker } from "@/components/ContactPicker";
import {
  Loader2,
  Users,
  Settings,
  LayoutGrid,
  UserPlus,
  Save,
  Trash2,
  Edit2,
  Key,
  X,
  Activity,
  AlertTriangle,
  Download,
  Contact2,
  RefreshCw,
  ArrowUp,
  CopyMinus
} from "lucide-react";


export function AdminPage() {
  const { isAdmin, user: currentUser } = useAuth();
  const [searchParams] = useSearchParams();
  const initialTabParam = searchParams.get('tab');
  const initialTab = initialTabParam === 'settings' || initialTabParam === 'modules' ? initialTabParam : 'users';
  const [activeTab, setActiveTab] = useState<"users" | "settings" | "modules">(initialTab);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [showAddUser, setShowAddUser] = useState(false);
  const [passwordUser, setPasswordUser] = useState<User | null>(null);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await adminApi.getUsers();
      setUsers(response.data.data);
    } catch (error) {
      console.error("Failed to load users:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateUser = async (userId: number, updates: Partial<User>) => {
    setIsSaving(true);
    try {
      await adminApi.updateUser(userId, updates);
      await loadUsers();
      setEditingUser(null);
    } catch (error) {
      console.error("Failed to update user:", error);
      alert("Fehler beim Speichern des Benutzers");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteUser = async (userId: number) => {
    if (!confirm('Sind Sie sicher, dass Sie diesen Benutzer löschen möchten?')) {
      return;
    }
    try {
      await adminApi.deleteUser(userId);
      await loadUsers();
    } catch (error) {
      console.error("Failed to delete user:", error);
      alert("Fehler beim Löschen des Benutzers");
    }
  };

  const handleChangePassword = async (userId: number, password: string) => {
    setIsChangingPassword(true);
    try {
      await adminApi.changePassword(userId, password);
      alert('Passwort erfolgreich geändert');
      setPasswordUser(null);
    } catch (error: any) {
      console.error("Failed to change password:", error);
      alert(error.response?.data?.message || "Fehler beim Ändern des Passworts");
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleAddUser = async (userData: {
    name: string;
    email: string;
    password?: string;
    role: 'user' | 'approver' | 'admin';
    is_active: boolean;
  }) => {
    setIsSaving(true);
    try {
      await adminApi.createUser(userData);
      await loadUsers();
      setShowAddUser(false);
    } catch (error: any) {
      console.error("Failed to create user:", error);
      alert(error.response?.data?.message || "Fehler beim Erstellen des Benutzers");
    } finally {
      setIsSaving(false);
    }
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'admin':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">Administrator</span>;
      case 'approver':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Genehmiger</span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Benutzer</span>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Verwaltung</h1>
          <p className="text-sm text-gray-500 mt-1">Benutzerverwaltung und Systemeinstellungen</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab("users")}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
              activeTab === "users"
                ? "border-primary-600 text-primary-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <Users className="h-4 w-4 mr-2" />
            Benutzer
          </button>
          
          {isAdmin && (
            <>
              <button
                onClick={() => setActiveTab("modules")}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
                  activeTab === "modules"
                    ? "border-primary-600 text-primary-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                <LayoutGrid className="h-4 w-4 mr-2" />
                Module
              </button>
              <button
                onClick={() => setActiveTab("settings")}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
                  activeTab === "settings"
                    ? "border-primary-600 text-primary-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                <Settings className="h-4 w-4 mr-2" />
                Einstellungen
              </button>
            </>
          )}
        </nav>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        </div>
      ) : activeTab === "users" ? (
        <UsersPanel
          users={users}
          currentUser={currentUser}
          isAdmin={isAdmin}
          onUpdate={handleUpdateUser}
          onDelete={handleDeleteUser}
          onEdit={setEditingUser}
          editingUser={editingUser}
          isSaving={isSaving}
          getRoleBadge={getRoleBadge}
          onAddUser={() => setShowAddUser(true)}
          onChangePassword={setPasswordUser}
        />
      ) : activeTab === "modules" ? (
        <ModulesPanel />
      ) : (
        <SettingsPanel />
      )}

      {/* Add User Modal */}
      {showAddUser && (
        <AddUserModal
          onClose={() => setShowAddUser(false)}
          onSubmit={handleAddUser}
          isSaving={isSaving}
        />
      )}
      
      {/* Password Change Modal */}
      {passwordUser && (
        <PasswordChangeModal
          user={passwordUser}
          onClose={() => setPasswordUser(null)}
          onSubmit={(password) => handleChangePassword(passwordUser.id, password)}
          isSaving={isChangingPassword}
        />
      )}
    </div>
  );
}

function UsersPanel({ 
  users, 
  currentUser, 
  isAdmin,
  onUpdate, 
  onDelete, 
  onEdit,
  editingUser,
  isSaving,
  getRoleBadge,
  onAddUser,
  onChangePassword
}: {
  users: User[];
  currentUser: User | null;
  isAdmin: boolean;
  onUpdate: (id: number, data: Partial<User>) => void;
  onDelete: (id: number) => void;
  onEdit: (user: User | null) => void;
  editingUser: User | null;
  isSaving: boolean;
  getRoleBadge: (role: string) => JSX.Element;
  onAddUser: () => void;
  onChangePassword: (user: User) => void;
}) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'user' as UserRole,
    is_active: true,
    exclude_from_sync: false,
    microsoft_365_email: '',
    microsoft_365_user_id: '',
    // Per-User-Modul-Overrides: {"module_key": "on"|"off"}, fehlender Key = erben
    module_overrides: {} as Record<string, 'on' | 'off'> | null,
    // Verknüpfter Master-Kontakt (Bauleiter-Zuordnung); null = keine Verknüpfung
    contact_id: null as number | null
  });
  // Anzeigename des verknüpften Kontakts (nur für die UI, wird nicht gespeichert)
  const [contactName, setContactName] = useState('');
  
  const [contactCounts, setContactCounts] = useState<Record<number, { count: number | null; loading: boolean; error?: string }>>({});
  const [exportingUserId, setExportingUserId] = useState<number | null>(null);
  const [wipingUserId, setWipingUserId] = useState<number | null>(null);
  const [wipeConfirmUser, setWipeConfirmUser] = useState<User | null>(null);
  const [wipeResult, setWipeResult] = useState<Record<number, {
    success: boolean;
    message: string;
    data?: any;
    error?: string;
  }>>({});

  // Outlook duplicates state. Detecting them is cheap (one indexed query);
  // we run it on mount for every user with an M365 ID, the same way we
  // fetch the live Outlook count. The result is used to render an inline
  // amber banner on the user card and to enable the dedupe button.
  const [duplicates, setDuplicates] = useState<Record<number, {
    loading: boolean;
    total: number;        // DB-level duplicate mappings count
    outlookExtra: number; // Outlook-level duplicate cards count
    hasDuplicates: boolean;
    message?: string;
    error?: string;
  }>>({});
  const [dedupeUserId, setDedupeUserId] = useState<number | null>(null);
  const [dedupeResult, setDedupeResult] = useState<Record<number, {
    success: boolean;
    message: string;
    data?: any;
    error?: string;
  }>>({});

  
  useEffect(() => {
    const fetchCounts = async () => {
      const counts: Record<number, { count: number | null; loading: boolean; error?: string }> = {};
      
      // Initialize all users with loading state
      users.forEach(u => {
        if (u.microsoft_365_user_id) {
          counts[u.id] = { count: null, loading: true };
        }
      });
      setContactCounts(counts);
      
      // Fetch counts in parallel
      const promises = users
        .filter(u => u.microsoft_365_user_id)
        .map(async (u) => {
          try {
            const response = await adminApi.getUserContactCount(u.id);
            return { id: u.id, data: response.data.data };
          } catch (err: any) {
            return { id: u.id, data: { count: null, message: 'Fehler' } };
          }
        });
      
      const results = await Promise.all(promises);

      setContactCounts(prev => {
        const next = { ...prev };
        results.forEach(r => {
          next[r.id] = { count: r.data.count, loading: false, error: r.data.message };
        });
        return next;
      });

      // Also fetch Outlook duplicate state for every M365 user. The same
      // map shape is used by the dedupe button below.
      const dupePromises = users
        .filter(u => u.microsoft_365_user_id)
        .map(async (u) => {
          try {
            const response = await adminApi.getUserOutlookDuplicates(u.id);
            const data = response.data.data;
            // The backend now reports both DB-level and Outlook-level
            // duplicates. We surface the combined number on the UI
            // and the dedupe button is enabled when either count is
            // non-zero. The button is the same one for both kinds,
            // because the dedupe endpoint handles both passes.
            const total = data.total_duplicates
              ?? ((data.total_duplicate_mappings ?? 0) + (data.outlook_extra_cards ?? 0));
            const hasDuplicates = data.has_duplicates ?? total > 0;
            return {
              id: u.id,
              total,
              outlookExtra: data.outlook_extra_cards ?? 0,
              hasDuplicates,
              message: data.message,
            };
          } catch (err: any) {
            return { id: u.id, total: 0, outlookExtra: 0, hasDuplicates: false, message: 'Fehler' };
          }
        });
      const dupeResults = await Promise.all(dupePromises);
      setDuplicates(prev => {
        const next = { ...prev };
        dupeResults.forEach(r => {
          next[r.id] = {
            loading: false,
            total: r.total,
            outlookExtra: r.outlookExtra,
            hasDuplicates: r.hasDuplicates,
            message: r.message,
          };
        });
        return next;
      });
    };
    
    if (users.length > 0) {
      fetchCounts();
    }
  }, [users]);
  
  const handleExportVcf = async (user: User) => {
    if (!user.microsoft_365_user_id) return;
    setExportingUserId(user.id);
    try {
      const response = await adminApi.exportUserContacts(user.id);
      const { filename, content } = response.data.data;
      
      // Decode base64 and trigger download
      const byteString = atob(content);
      const bytes = new Uint8Array(byteString.length);
      for (let i = 0; i < byteString.length; i++) {
        bytes[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: 'text/vcard;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Export fehlgeschlagen');
    } finally {
      setExportingUserId(null);
    }
  };

  const handleWipeOutlookOnly = async (user: User) => {
    if (!user.microsoft_365_user_id) return;
    setWipingUserId(user.id);
    setWipeResult(prev => {
      const next = { ...prev };
      delete next[user.id];
      return next;
    });
    try {
      const response = await adminApi.wipeUserOutlookOnly(user.id);
      const data = response.data.data || {};
      if (data.status === 'started' && data.log_id) {
        setWipeResult(prev => ({
          ...prev,
          [user.id]: {
            success: true,
            message: 'Wipe läuft im Hintergrund (Log-ID ' + data.log_id + '). Ergebnis folgt in Kürze in den Sync-Logs.',
            data: { status: 'running', log_id: data.log_id, sync_email: data.sync_email, mode: 'wipe_only' },
          },
        }));
        let polls = 0;
        const poll = setInterval(async () => {
          polls++;
          try {
            const logResp = await syncApi.getLogs(1);
            const entry = (logResp.data.data || []).find((l: any) => l.id === data.log_id);
            if (entry && (entry.status === 'success' || entry.status === 'failed')) {
              clearInterval(poll);
              const details = (entry.details && typeof entry.details === 'object') ? entry.details : {};
              setWipeResult(prev => ({
                ...prev,
                [user.id]: {
                  success: entry.status === 'success',
                  message: String(details.summary || (entry.status === 'success' ? 'Abgeschlossen' : 'Fehlgeschlagen')),
                  data: details,
                },
              }));
              try {
                const cnt = await adminApi.getUserContactCount(user.id);
                setContactCounts(prev => ({
                  ...prev,
                  [user.id]: { count: cnt.data.data.count, loading: false, error: cnt.data.data.message },
                }));
              } catch { /* best effort */ }
            } else if (polls > 60) {
              clearInterval(poll);
            }
          } catch {
            // ignore
          }
        }, 5000);
      } else {
        setWipeResult(prev => ({
          ...prev,
          [user.id]: {
            success: !!response.data.success,
            message: response.data.message || 'Fertig',
            data: response.data.data,
          },
        }));
        try {
          const cnt = await adminApi.getUserContactCount(user.id);
          setContactCounts(prev => ({
            ...prev,
            [user.id]: { count: cnt.data.data.count, loading: false, error: cnt.data.data.message },
          }));
        } catch { /* ignore */ }
      }
    } catch (err: any) {
      setWipeResult(prev => ({
        ...prev,
        [user.id]: {
          success: false,
          message: err.response?.data?.message || 'Wipe fehlgeschlagen',
          error: err.message,
        },
      }));
    } finally {
      setWipingUserId(null);
    }
  };



  const [pushingUserId, setPushingUserId] = useState<number | null>(null);
  const [pushResult, setPushResult] = useState<Record<number, { success: boolean; message: string; data?: any }>>({});
  const handlePushFromDb = async (user: User) => {
    if (!user.microsoft_365_user_id) return;
    if (!confirm(`Alle Master-Kontakte aus der DB in das Outlook von ${user.name} (${user.microsoft_365_email || user.email}) übertragen?\n\nBestehende Outlook-Kontakte dieses Benutzers werden NICHT gelöscht — bereits vorhandene werden per PATCH aktualisiert.`)) return;
    setPushingUserId(user.id);
    try {
      const response = await adminApi.pushMasterContactsToUser(user.id);
      setPushResult(prev => ({
        ...prev,
        [user.id]: {
          success: !!response.data.success,
          message: response.data.message || 'Fertig',
          data: response.data.data,
        },
      }));
      try {
        const cnt = await adminApi.getUserContactCount(user.id);
        setContactCounts(prev => ({
          ...prev,
          [user.id]: { count: cnt.data.data.count, loading: false, error: cnt.data.data.message },
        }));
      } catch { /* ignore */ }
    } catch (err: any) {
      setPushResult(prev => ({
        ...prev,
        [user.id]: {
          success: false,
          message: err.response?.data?.message || 'Push fehlgeschlagen',
        },
      }));
    } finally {
      setPushingUserId(null);
    }
  };

  /**
   * Dedupe Outlook contact cards for a user. Fire-and-forget: returns a
   * log_id that we poll every 5s for status updates. On success we
   * refresh both the contact count and the duplicate counter.
   */
  const handleDedupeDuplicates = async (user: User) => {
    if (!user.microsoft_365_user_id) return;
    const dupeCount = duplicates[user.id]?.total ?? 0;
    if (dupeCount === 0) return;
    if (!confirm(
      `Outlook-Duplikate für ${user.name} (${user.microsoft_365_email || user.email}) bereinigen?\n\n`
      + `Es werden ${dupeCount} Duplikat-Karten in Outlook gelöscht und ${dupeCount} verwaiste contact_mapping-Zeilen entfernt.\n`
      + 'Pro Karte wird 1 Graph-API-Call abgesetzt — bei vielen Duplikaten kann das ein paar Minuten dauern.'
    )) return;
    setDedupeUserId(user.id);
    try {
      const response = await adminApi.dedupeUserOutlookDuplicates(user.id);
      const logId = response.data.data?.log_id;
      // Poll the sync_logs row to refresh the count and the dupe counter
      // once the background job finishes.
      let polls = 0;
      const poll = setInterval(async () => {
        polls++;
        try {
          // Use the by-id log endpoint so the background job can be
          // observed independently of the global /sync/status view.
          const resp = await syncApi.getLog(logId);
          const status = resp.data?.data;
          if (status && (status.status === 'success' || status.status === 'partial' || status.status === 'failed')) {
            clearInterval(poll);
            const details = status.details ?? {};
            setDedupeResult(prev => ({
              ...prev,
              [user.id]: {
                success: status.status !== 'failed',
                message: status.status === 'failed'
                  ? 'Bereinigung fehlgeschlagen'
                  : (status.status === 'partial'
                      ? 'Bereinigung mit Fehlern abgeschlossen'
                      : 'Bereinigung erfolgreich abgeschlossen'),
                data: details,
              },
            }));
            // Refresh contact count + duplicate counter.
            try {
              const cnt = await adminApi.getUserContactCount(user.id);
              setContactCounts(prev => ({
                ...prev,
                [user.id]: { count: cnt.data.data.count, loading: false, error: cnt.data.data.message },
              }));
            } catch { /* best effort */ }
            try {
              const dup = await adminApi.getUserOutlookDuplicates(user.id);
              const data = dup.data.data;
              const total = data.total_duplicates
                ?? ((data.total_duplicate_mappings ?? 0) + (data.outlook_extra_cards ?? 0));
              setDuplicates(prev => ({
                ...prev,
                [user.id]: {
                  loading: false,
                  total,
                  outlookExtra: data.outlook_extra_cards ?? 0,
                  hasDuplicates: data.has_duplicates ?? total > 0,
                  message: data.message,
                },
              }));
            } catch { /* best effort */ }
          } else if (polls > 60) {
            clearInterval(poll);
          }
        } catch { /* ignore */ }
      }, 5000);
    } catch (err: any) {
      setDedupeResult(prev => ({
        ...prev,
        [user.id]: {
          success: false,
          message: err.response?.data?.message || 'Bereinigung fehlgeschlagen',
        },
      }));
    } finally {
      setDedupeUserId(null);
    }
  };

  const handleEdit = (user: User) => {
    onEdit(user);
    setFormData({
      name: user.name,
      email: user.email,
      role: user.role,
      is_active: user.is_active ?? true,
      exclude_from_sync: user.exclude_from_sync ?? false,
      microsoft_365_email: user.microsoft_365_email || '',
      microsoft_365_user_id: user.microsoft_365_user_id || '',
      module_overrides: { ...(user.module_overrides ?? {}) },
      contact_id: user.contact_id ?? null
    });
    setContactName(user.contact_name || '');
  };

  const handleSave = () => {
    if (editingUser) {
      // Modul-Overrides nur mitschicken, wenn sie geändert wurden — sonst
      // würde ein reines Namens-Update bestehende Overrides auf null setzen.
      const overridesChanged =
        JSON.stringify(formData.module_overrides ?? {}) !== JSON.stringify(editingUser.module_overrides ?? {});
      // Kontakt-Verknüpfung genauso: nur bei Änderung mitschicken (und null
      // muss explizit übermittelt werden können, um die Verknüpfung zu leeren).
      const contactChanged = formData.contact_id !== (editingUser.contact_id ?? null);
      const { module_overrides, contact_id, ...rest } = formData;
      const payload: Partial<User> = { ...rest };
      if (overridesChanged) payload.module_overrides = module_overrides;
      if (contactChanged) payload.contact_id = contact_id;
      onUpdate(editingUser.id, payload);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Benutzer ({users.length})</h3>
          <p className="text-sm text-gray-500">Verwalten Sie Benutzer und deren Berechtigungen</p>
        </div>
        
        {isAdmin && (
          <button 
            onClick={onAddUser}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Benutzer hinzufügen
          </button>
        )}
      </div>
      
      <div className="divide-y divide-gray-200">
        {users.map((user) => (
          <div key={user.id} className="p-6">
            {editingUser?.id === user.id ? (
              /* Edit Mode */
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">E-Mail</label>
                    <input
                      type="email"
                      value={formData.email}
                      disabled
                      className="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 text-gray-500 sm:text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Rolle</label>
                    <select
                      value={formData.role}
                      onChange={(e) => setFormData({...formData, role: e.target.value as 'user' | 'approver' | 'admin'})}
                      disabled={!isAdmin || user.id === currentUser?.id}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    >
                      <option value="user">Benutzer</option>
                      <option value="approver">Genehmiger</option>
                      <option value="admin">Administrator</option>
                    </select>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                      disabled={user.id === currentUser?.id}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900">Aktiv</label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.exclude_from_sync}
                      onChange={(e) => setFormData({...formData, exclude_from_sync: e.target.checked})}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900">Vom Sync ausschließen</label>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Outlook E-Mail (M365)</label>
                    <input
                      type="email"
                      value={formData.microsoft_365_email}
                      onChange={(e) => setFormData({...formData, microsoft_365_email: e.target.value})}
                      placeholder="z.B. admin@gpsbau.de"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500">Die Outlook-E-Mail des Benutzers für den Kontakt-Sync.</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Azure AD Object ID</label>
                    <input
                      type="text"
                      value={formData.microsoft_365_user_id}
                      onChange={(e) => setFormData({...formData, microsoft_365_user_id: e.target.value})}
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm font-mono"
                    />
                    <p className="mt-1 text-xs text-gray-500">Die Azure AD Object ID. Wichtig für Alias-E-Mail-Adressen.</p>
                  </div>

                  {isAdmin && (
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">Verknüpfter Kontakt</label>
                      <p className="mt-1 text-xs text-gray-500">
                        Optional: Master-Kontakt dieses Benutzers (z. B. für die Bauleiter-Zuordnung in Bauvorhaben).
                      </p>
                      <div className="mt-2 flex items-center gap-3">
                        {formData.contact_id ? (
                          <>
                            <Link
                              to={`/contacts/${formData.contact_id}`}
                              className="text-sm font-medium text-primary-600 hover:text-primary-700"
                            >
                              {contactName || `Kontakt #${formData.contact_id}`}
                            </Link>
                            <button
                              type="button"
                              onClick={() => {
                                setFormData({ ...formData, contact_id: null });
                                setContactName('');
                              }}
                              className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs text-gray-600 rounded hover:bg-gray-50"
                            >
                              <X className="h-3 w-3 mr-1" />
                              Verknüpfung aufheben
                            </button>
                          </>
                        ) : (
                          <span className="text-sm text-gray-400">— Kein Kontakt verknüpft</span>
                        )}
                      </div>
                      <div className="mt-2">
                        <ContactPicker
                          onSelect={(c) => {
                            setFormData({ ...formData, contact_id: c.id });
                            setContactName(c.display_name || c.company_name || c.email_address || `Kontakt #${c.id}`);
                          }}
                          placeholder="Kontakt suchen…"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {isAdmin && (
                  <div className="border-t border-gray-100 pt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Module</label>
                    <p className="text-xs text-gray-500 mb-3">
                      Individuelle Zugriffe dieses Benutzers. „Standard (Rolle)" folgt dem Rollen-Standard
                      aus der Modul-Verwaltung; ein global deaktiviertes Modul bleibt für ihn aus.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {MODULE_DEFS.map((def) => {
                        const value = formData.module_overrides?.[def.key] ?? '';
                        return (
                          <label key={def.key} className="block">
                            <span className="text-sm text-gray-600">{def.label}</span>
                            <select
                              value={value}
                              onChange={(e) => {
                                const newValue = e.target.value;
                                const next = { ...(formData.module_overrides ?? {}) };
                                if (newValue === 'on' || newValue === 'off') {
                                  next[def.key] = newValue;
                                } else {
                                  delete next[def.key];
                                }
                                setFormData({ ...formData, module_overrides: next });
                              }}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                            >
                              <option value="">Standard (Rolle)</option>
                              <option value="on">Aktiviert</option>
                              <option value="off">Deaktiviert</option>
                            </select>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="flex gap-2">
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                  >
                    {isSaving ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4 mr-1" />
                    )}
                    Speichern
                  </button>
                  <button
                    onClick={() => onEdit(null)}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Abbrechen
                  </button>
                </div>
              </div>
            ) : (
              /* View Mode */
              <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                <div className="flex items-start gap-4 flex-1 min-w-0">
                  <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-primary-600 font-medium">{user.name.charAt(0).toUpperCase()}</span>
                  </div>
                  
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-medium text-gray-900">{user.name}</p>
                      {getRoleBadge(user.role)}
                      {!user.is_active && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Inaktiv
                        </span>
                      )}
                      {user.is_active && user.microsoft_365_user_id ? (
                        user.exclude_from_sync ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800" title="Sync deaktiviert – Kontakte werden nicht synchronisiert">
                            Sync ausgeschlossen
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Sync aktiv
                          </span>
                        )
                      ) : user.is_active ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600" title="Azure AD Object ID fehlt – Kontakt-Sync nicht möglich">
                          Sync inaktiv
                        </span>
                      ) : null}
                      {/* One-click exclude toggle. Visible for every user
                          with an M365 ID and is_active=TRUE so the admin
                          can quickly exclude themselves (or anyone else)
                          from the next sync run without entering the full
                          edit modal. The toggle is intentionally small and
                          sits next to the status badge so the admin can
                          see the current state at a glance. */}
                      {isAdmin && user.is_active && user.microsoft_365_user_id && (
                        <label
                          className="inline-flex items-center gap-1 cursor-pointer text-xs text-gray-600 hover:text-gray-800"
                          title={user.exclude_from_sync
                            ? 'Sync ist deaktiviert. Aktivieren, um diesen Benutzer wieder in den nächsten Sync einzubeziehen.'
                            : 'Sync ist aktiv. Deaktivieren, um diesen Benutzer vom nächsten Sync auszuschließen (Push und Pull werden übersprungen).'}
                        >
                          <input
                            type="checkbox"
                            checked={!user.exclude_from_sync}
                            onChange={(e) => {
                              const newExclude = !e.target.checked;
                              if (newExclude && !confirm(
                                `Sync für ${user.name} deaktivieren?\n\n`
                                + 'Ab dem nächsten Sync werden keine Kontakte mehr zu/von diesem Outlook übertragen. '
                                + 'Bestehende Kontakte im Outlook und in der DB bleiben unverändert.'
                              )) return;
                              onUpdate(user.id, { exclude_from_sync: newExclude });
                            }}
                            className="h-3.5 w-3.5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          />
                          <span className="select-none">
                            {user.exclude_from_sync ? 'Sync deaktiviert' : 'Sync aktiv'}
                          </span>
                        </label>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 truncate">{user.email}</p>
                    {user.microsoft_365_email && (
                      <p className="text-xs text-gray-400 truncate">Outlook: {user.microsoft_365_email}</p>
                    )}
                    {user.microsoft_365_user_id && (
                      <p className="text-xs text-gray-400 font-mono truncate">Object ID: {user.microsoft_365_user_id}</p>
                    )}
                    {contactCounts[user.id]?.loading && (
                      <p className="text-xs text-gray-400 flex items-center gap-1">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Kontakte zählen...
                      </p>
                    )}
                    {contactCounts[user.id]?.count !== undefined && contactCounts[user.id]?.count !== null && (
                      <p className="text-xs text-blue-600 font-medium">
                        <Contact2 className="h-3 w-3 inline mr-1" />
                        {contactCounts[user.id].count} Kontakt{contactCounts[user.id].count !== 1 ? 'e' : ''} in Outlook
                      </p>
                    )}
                    {contactCounts[user.id]?.error && !contactCounts[user.id]?.loading && contactCounts[user.id]?.count === null && (
                      <p className="text-xs text-amber-600">{contactCounts[user.id].error}</p>
                    )}
                    {/* Outlook-duplicates indicator. Shows the same number
                        the dedupe button will operate on, so the admin
                        can see the cost before clicking. Only shown
                        for users with an M365 ID and at least 1
                        duplicate mapping. */}
                    {user.microsoft_365_user_id && duplicates[user.id]?.hasDuplicates && (
                      <p className="text-xs text-amber-700 font-medium flex items-center gap-1" title="Doppelte Outlook-Kontakte fuer diesen Benutzer (DB-Mappings + reine Outlook-Duplikate)">
                        <AlertTriangle className="h-3 w-3" />
                        {duplicates[user.id].total} Outlook-Duplikate
                        {duplicates[user.id].outlookExtra > 0 && duplicates[user.id].total > duplicates[user.id].outlookExtra && (
                          <span className="text-gray-500 font-normal"> (davon {duplicates[user.id].outlookExtra} in Outlook ohne DB-Mapping)</span>
                        )}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                  {user.microsoft_365_user_id && (
                    <button
                      onClick={() => handleExportVcf(user)}
                      disabled={exportingUserId === user.id}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md"
                      title="Kontakte als VCF herunterladen"
                    >
                      {exportingUserId === user.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4" />
                      )}
                    </button>
                  )}
                  {isAdmin && user.microsoft_365_user_id && (
                    <button
                      onClick={() => setWipeConfirmUser(user)}
                      disabled={wipingUserId === user.id}
                      className="p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-md"
                      title="Outlook löschen (kein Push)"
                    >
                      {wipingUserId === user.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4" />
                      )}
                    </button>
                  )}
                  {isAdmin && user.microsoft_365_user_id && (
                    <button
                      onClick={() => handlePushFromDb(user)}
                      disabled={pushingUserId === user.id || wipingUserId === user.id}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md"
                      title="DB-Kontakte in Outlook schieben (kein Löschen)"
                    >
                      {pushingUserId === user.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <ArrowUp className="h-4 w-4" />
                      )}
                    </button>
                  )}
                  {/* Outlook duplicates dedupe. Enabled when the
                      diagnostic reports duplicates in either the DB
                      (multiple contact_mapping rows per master
                      contact) or in Outlook itself (multiple
                      cards with the same fingerprint). The button
                      is intentionally amber to make this rare but
                      important action discoverable. */}
                  {isAdmin && user.microsoft_365_user_id && (
                    <button
                      onClick={() => handleDedupeDuplicates(user)}
                      disabled={dedupeUserId === user.id || !duplicates[user.id]?.hasDuplicates}
                      className={`p-2 rounded-md ${duplicates[user.id]?.hasDuplicates ? "text-amber-600 hover:text-amber-800 hover:bg-amber-50" : "text-gray-300 cursor-not-allowed"}`}
                      title={duplicates[user.id]?.hasDuplicates
                        ? `${duplicates[user.id]?.total} Outlook-Duplikate entfernen`
                        : 'Keine Duplikate gefunden'}
                    >
                      {dedupeUserId === user.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <CopyMinus className="h-4 w-4" />
                      )}
                    </button>
                  )}
                  <button
                    onClick={() => onChangePassword(user)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md"
                    title="Passwort ändern"
                  >
                    <Key className="h-4 w-4" />
                  </button>
                  {isAdmin && user.id !== currentUser?.id && (
                    <>
                      <button
                        onClick={() => handleEdit(user)}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md"
                        title="Bearbeiten"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      
                      <button
                        onClick={() => onDelete(user.id)}
                        className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-md"
                        title="Löschen"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
        {wipeResult[user.id] && (
          <div className={`mt-3 rounded-md p-3 text-sm border ${
            wipeResult[user.id].success
              ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}>
            <p className="font-medium">
              {wipeResult[user.id].success ? '✅ ' : '❌ '}
              {wipeResult[user.id].message}
            </p>
            {wipeResult[user.id].data && (
              <div className="mt-2 text-xs space-y-1">
                <p>Outlook gelöscht: <strong>{wipeResult[user.id].data.outlook_contacts_deleted ?? '–'}</strong> (Fehler: {wipeResult[user.id].data.outlook_delete_failed ?? '–'})</p>
                {wipeResult[user.id].data.orphan_mappings_cleared !== undefined && (
                  <p>Verwaiste contact_mapping-Zeilen entfernt: <strong>{wipeResult[user.id].data.orphan_mappings_cleared}</strong></p>
                )}
                {wipeResult[user.id].data.db_contacts_pushed !== undefined && (
                  <p>Aus DB neu angelegt: <strong>{wipeResult[user.id].data.db_contacts_pushed}</strong> (Fehler: {wipeResult[user.id].data.db_create_failed ?? '–'})</p>
                )}
                {Array.isArray(wipeResult[user.id].data.log) && wipeResult[user.id].data.log.length > 0 && (
                  <details className="mt-1">
                    <summary className="cursor-pointer text-gray-600">Log anzeigen ({wipeResult[user.id].data.log.length})</summary>
                    <ul className="mt-1 ml-4 list-disc">
                      {wipeResult[user.id].data.log.slice(0, 20).map((line: string, i: number) => (
                        <li key={i}>{line}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}
          </div>
        )}
        {dedupeResult[user.id] && (
          <div className={`mt-2 rounded-md p-3 text-sm border ${
            dedupeResult[user.id].success
              ? 'bg-amber-50 border-amber-200 text-amber-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}>
            <p className="font-medium">
              {dedupeResult[user.id].success ? '🧹 ' : '❌ '}
              Outlook-Duplikate: {dedupeResult[user.id].message}
            </p>
            {dedupeResult[user.id].data && (
              <div className="mt-1 text-xs space-y-1">
                <p>In Outlook gelöscht: <strong>{dedupeResult[user.id].data.outlook_contacts_deleted ?? '–'}</strong> (Fehler: {dedupeResult[user.id].data.outlook_delete_failed ?? '–'})</p>
                <p>contact_mapping-Zeilen entfernt: <strong>{dedupeResult[user.id].data.mappings_deleted ?? '–'}</strong></p>
              </div>
            )}
          </div>
        )}
        {pushResult[user.id] && (
          <div className={`mt-2 rounded-md p-3 text-sm border ${
            pushResult[user.id].success
              ? 'bg-blue-50 border-blue-200 text-blue-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}>
            <p className="font-medium">
              {pushResult[user.id].success ? '⬆️ ' : '❌ '}
              Push aus DB: {pushResult[user.id].message}
            </p>
            {pushResult[user.id].data && (
              <div className="mt-1 text-xs space-y-1">
                <p>Neu angelegt: <strong>{pushResult[user.id].data.created}</strong> · Aktualisiert: <strong>{pushResult[user.id].data.updated}</strong> · Fehler: <strong>{pushResult[user.id].data.failed}</strong> (von {pushResult[user.id].data.master_contacts_total} Master-Kontakten)</p>
                {Array.isArray(pushResult[user.id].data.log) && pushResult[user.id].data.log.length > 0 && (
                  <details className="mt-1">
                    <summary className="cursor-pointer text-gray-600">Fehler-Log anzeigen</summary>
                    <ul className="mt-1 ml-4 list-disc">
                      {pushResult[user.id].data.log.slice(0, 20).map((line: string, i: number) => (
                        <li key={i}>{line}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}
          </div>
        )}
          </div>
        ))}
      </div>
      {wipeConfirmUser && (
        <WipeConfirmModal
          user={wipeConfirmUser}
          onClose={() => setWipeConfirmUser(null)}
          onConfirm={() => {
            const u = wipeConfirmUser;
            setWipeConfirmUser(null);
            handleWipeOutlookOnly(u);
          }}
          isWorking={wipingUserId === wipeConfirmUser.id}
        />
      )}
    </div>
  );
}

function WipeConfirmModal({
  user,
  onClose,
  onConfirm,
  isWorking
}: {
  user: User;
  onClose: () => void;
  onConfirm: () => void;
  isWorking: boolean;
}) {
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [typed, setTyped] = useState('');
  const canConfirm = confirmChecked && typed === 'WIPE';
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            Outlook löschen
          </h3>
          <button onClick={onClose} disabled={isWorking} className="text-gray-400 hover:text-gray-500">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-700">
            Du bist dabei, <strong>alle Kontakte</strong> im Outlook-Postfach von{' '}
            <strong>{user.name}</strong> ({user.microsoft_365_email || user.email}) zu löschen.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-900">
            <p className="font-medium">⚠️ Das ist nicht widerrufbar.</p>
            <ul className="mt-1 list-disc ml-5 space-y-1">
              <li>Bestehende Outlook-Kontakte dieses Benutzers werden endgültig gelöscht.</li>
              <li>Die Zuordnungen (contact_mapping) für diesen Benutzer werden zurückgesetzt.</li>
              <li><strong>Es wird kein Push aus der Datenbank ausgelöst.</strong> Das Postfach bleibt leer, bis du es mit dem ⬆️-Button („DB-Kontakte in Outlook schieben") neu befüllst.</li>
            </ul>
          </div>
          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={confirmChecked}
              onChange={(e) => setConfirmChecked(e.target.checked)}
              disabled={isWorking}
              className="mt-1 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
            />
            <span>Ich verstehe, dass alle aktuellen Outlook-Kontakte dieses Benutzers gelöscht werden und das Postfach danach leer ist.</span>
          </label>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Zur Bestätigung <code className="bg-gray-100 px-1 rounded">WIPE</code> eintippen
            </label>
            <input
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              disabled={isWorking}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm"
              placeholder="WIPE"
              autoComplete="off"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              onClick={onConfirm}
              disabled={!canConfirm || isWorking}
              className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {isWorking ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Lösche...
                </>
              ) : (
                'Outlook jetzt löschen'
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={isWorking}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Abbrechen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PasswordChangeModal({
  user,
  onClose,
  onSubmit,
  isSaving
}: {
  user: User;
  onClose: () => void;
  onSubmit: (password: string) => void;
  isSaving: boolean;
}) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (password.length < 6) {
      setError('Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwörter stimmen nicht überein');
      return;
    }
    
    onSubmit(password);
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Passwort ändern</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <p className="text-sm text-gray-600">
            Passwort für <strong>{user.name}</strong> ändern
          </p>
          
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">{error}</div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700">Neues Passwort</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              placeholder="Mindestens 6 Zeichen"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">Passwort bestätigen</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              placeholder=""
              required
            />
          </div>
          
          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={isSaving}
              className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Speichern...
                </>
              ) : (
                'Passwort ändern'
              )}
            </button>
            
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Abbrechen
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AddUserModal({
  onClose,
  onSubmit,
  isSaving
}: {
  onClose: () => void;
  onSubmit: (data: { name: string; email: string; password?: string; role: 'user' | 'approver' | 'admin'; is_active: boolean; microsoft_365_email?: string; microsoft_365_user_id?: string }) => void;
  isSaving: boolean;
}) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user' as UserRole,
    is_active: true,
    microsoft_365_email: '',
    microsoft_365_user_id: ''
  });
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!formData.name.trim()) {
      setError('Name ist erforderlich');
      return;
    }
    if (!formData.email.trim()) {
      setError('E-Mail ist erforderlich');
      return;
    }

    
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Neuen Benutzer hinzufügen</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">{error}</div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700">Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              placeholder="Max Mustermann"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">E-Mail *</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              placeholder="max@example.com"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">Passwort (optional)</label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              placeholder=""
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">Rolle</label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({...formData, role: e.target.value as 'user' | 'approver' | 'admin'})}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            >
              <option value="user">Benutzer</option>
              <option value="approver">Genehmiger</option>
              <option value="admin">Administrator</option>
            </select>
          </div>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label className="ml-2 block text-sm text-gray-900">Aktiv</label>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">Outlook E-Mail (M365)</label>
            <input
              type="email"
              value={formData.microsoft_365_email}
              onChange={(e) => setFormData({...formData, microsoft_365_email: e.target.value})}
              placeholder="z.B. admin@gpsbau.de"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">Azure AD Object ID</label>
            <input
              type="text"
              value={formData.microsoft_365_user_id}
              onChange={(e) => setFormData({...formData, microsoft_365_user_id: e.target.value})}
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm font-mono"
            />
            <p className="mt-1 text-xs text-gray-500">
              Erforderlich für Kontakt-Sync. In Azure AD unter Benutzer → Active users → Profil → Object ID zu finden.
            </p>
          </div>
          
          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={isSaving}
              className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Speichern...
                </>
              ) : (
                'Benutzer erstellen'
              )}
            </button>
            
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Abbrechen
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RejectedContactsSection() {
  const [rows, setRows] = useState<any[]>([]);
  const [pagination, setPagination] = useState<any>({ total: 0, page: 1, per_page: 50, total_pages: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [filterEmail, setFilterEmail] = useState('');
  const [isForgetting, setIsForgetting] = useState(false);
  const [message, setMessage] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const load = async (page = 1) => {
    setIsLoading(true);
    try {
      const r = await adminApi.listRejectedContacts({ page, per_page: 50, user_email: filterEmail || undefined });
      setRows(r.data.data || []);
      setPagination(r.data.pagination || { total: 0, page: 1, per_page: 50, total_pages: 0 });
      setSelected(new Set());
    } catch (err: any) {
      setMessage('Fehler beim Laden: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { load(1); }, []);

  const toggleOne = (id: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const forgetSelected = async () => {
    if (selected.size === 0) return;
    if (!confirm(`${selected.size} Eintrag/Einträge wirklich entfernen? Beim nächsten Sync werden diese Kontakte wieder vorgeschlagen.`)) return;
    setIsForgetting(true);
    setMessage('');
    try {
      const r = await adminApi.forgetRejectedContacts(Array.from(selected));
      setMessage(r.data.message || `${selected.size} entfernt`);
      await load(pagination.page);
    } catch (err: any) {
      setMessage('Fehler: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsForgetting(false);
    }
  };

  const forgetOne = async (id: number) => {
    if (!confirm('Diesen Eintrag wirklich entfernen?')) return;
    setIsForgetting(true);
    setMessage('');
    try {
      const r = await adminApi.forgetRejectedContacts([id]);
      setMessage(r.data.message || 'Eintrag entfernt');
      await load(pagination.page);
    } catch (err: any) {
      setMessage('Fehler: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsForgetting(false);
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <h4 className="text-sm font-medium text-gray-900 mb-1">Abgelehnte Kontakte (Sync-Gedächtnis)</h4>
      <p className="text-sm text-gray-500 mb-3">
        Wenn ein Admin einen Antrag ablehnt, merkt sich die App diesen Kontakt zusammen mit dem Postfach,
        aus dem er kam. Beim nächsten Sync wird der gleiche Kontakt in diesem Postfach automatisch gelöscht
        statt erneut vorgeschlagen. Hier kannst du das Gedächtnis einsehen und einzelne Einträge zurücksetzen.
      </p>
      <div className="flex gap-2 items-center mb-3 flex-wrap">
        <input
          type="text"
          placeholder="Filtern nach Outlook-E-Mail (leer = alle)"
          value={filterEmail}
          onChange={(e) => setFilterEmail(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') load(1); }}
          className="block w-72 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-3 py-2 border"
        />
        <button
          type="button"
          onClick={() => load(1)}
          disabled={isLoading}
          className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
        >
          Filtern
        </button>
        {selected.size > 0 && (
          <button
            type="button"
            onClick={forgetSelected}
            disabled={isForgetting}
            className="inline-flex items-center px-3 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
          >
            {isForgetting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
            Auswahl entfernen ({selected.size})
          </button>
        )}
      </div>
      {message && (
        <div className="mb-2 text-sm text-gray-700">{message}</div>
      )}
      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Lade...
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500">Keine Einträge. Sobald du einen Antrag ablehnst, erscheint er hier.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-left text-gray-500 text-xs uppercase">
              <tr>
                <th className="py-2 pr-2"><input type="checkbox" checked={selected.size === rows.length && rows.length > 0} onChange={(e) => setSelected(new Set(e.target.checked ? rows.map(r => r.id) : []))} /></th>
                <th className="py-2 pr-2">Kontakt</th>
                <th className="py-2 pr-2">Outlook</th>
                <th className="py-2 pr-2">Aktion</th>
                <th className="py-2 pr-2">Abgelehnt</th>
                <th className="py-2 pr-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map(r => (
                <tr key={r.id}>
                  <td className="py-2 pr-2"><input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleOne(r.id)} /></td>
                  <td className="py-2 pr-2">
                    <div className="font-medium text-gray-900">{r.display_name || '—'}</div>
                    {r.email_address && <div className="text-xs text-gray-500">{r.email_address}</div>}
                  </td>
                  <td className="py-2 pr-2 text-gray-700">{r.user_email}</td>
                  <td className="py-2 pr-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      r.action === 'delete' ? 'bg-red-100 text-red-800'
                        : r.action === 'update' ? 'bg-amber-100 text-amber-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {r.action}
                    </span>
                  </td>
                  <td className="py-2 pr-2 text-gray-500">{new Date(r.rejected_at).toLocaleString('de-DE')}</td>
                  <td className="py-2 pr-2 text-right">
                    <button
                      type="button"
                      onClick={() => forgetOne(r.id)}
                      className="text-xs text-red-600 hover:text-red-800"
                    >
                      entfernen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {pagination.total_pages > 1 && (
            <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
              Seite {pagination.page} von {pagination.total_pages} ({pagination.total} Einträge)
              <button onClick={() => load(Math.max(1, pagination.page - 1))} disabled={pagination.page <= 1} className="px-2 py-1 border rounded disabled:opacity-50">‹</button>
              <button onClick={() => load(Math.min(pagination.total_pages, pagination.page + 1))} disabled={pagination.page >= pagination.total_pages} className="px-2 py-1 border rounded disabled:opacity-50">›</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function GraphTestSection() {
  const [testResult, setTestResult] = useState<any>(null);
  const [isTesting, setIsTesting] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [clearMessage, setClearMessage] = useState('');
  const [isPurging, setIsPurging] = useState(false);
  const [purgeMessage, setPurgeMessage] = useState('');
  const [isPurgingDb, setIsPurgingDb] = useState(false);
  const [purgeDbMessage, setPurgeDbMessage] = useState('');
  const [purgeDbPassword, setPurgeDbPassword] = useState('');
  
  const runTest = async () => {
    setIsTesting(true);
    setTestResult(null);
    setClearMessage('');
    setPurgeMessage('');
    try {
      const response = await syncApi.test('');
      setTestResult(response.data);
    } catch (err: any) {
      const backend = err.response?.data;
      const rawText = err.response?.request?.responseText || '';

      let msg = '';
      let raw = '';
      
      if (rawText && typeof rawText === 'string' && rawText.length > 0) {
        raw = rawText.substring(0, 500);
      }
      
      if (typeof backend === 'string' && backend) {
        msg = 'Server returned HTML instead of JSON.';
      } else if (backend && typeof backend === 'object') {
        msg = backend.message || backend.error || JSON.stringify(backend);
      } else if (err.message) {
        msg = err.message;
      }
      
      setTestResult({
        success: false,
        message: (msg || 'Unbekannter Fehler') + (raw ? ' | RAW: ' + raw : '')
      });
    } finally {
      setIsTesting(false);
    }
  };
  
  const clearAllDelta = async () => {
    if (!confirm('Delta-Links löschen? Dies erzwingt beim nächsten Sync einen vollständigen Abruf aller Kontakte.')) return;
    setIsClearing(true);
    setClearMessage('');
    try {
      const response = await syncApi.clearDelta();
      setClearMessage(response.data.message || 'Delta-Links gelöscht');
    } catch (err: any) {
      setClearMessage('Fehler: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsClearing(false);
    }
  };
  
  const purgeAndReset = async () => {
    if (!confirm('WARNUNG: Alle Synchronisationsdaten zurücksetzen?\n\nDies löscht:\n• Alle Kontakt-Zuordnungen zu Outlook\n• Alle Delta-Links\n• Alle Anträge\n• Alle Sync-Logs\n\nDie Kontaktdaten in der App bleiben erhalten.\n\nNächster Sync startet komplett frisch.')) return;
    setIsPurging(true);
    setPurgeMessage('');
    try {
      const response = await syncApi.purgeAndReset();
      setPurgeMessage(response.data.message || 'Zurückgesetzt');
    } catch (err: any) {
      setPurgeMessage('Fehler: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsPurging(false);
    }
  };
  
  const purgeDatabase = async () => {
    if (!purgeDbPassword) {
      setPurgeDbMessage('Bitte geben Sie Ihr Passwort ein');
      return;
    }
    if (!confirm('⚠️ EXTREM WARNUNG ⚠️\n\nDies löscht ALLE Datenbank-Daten:\n• Alle Kontakte\n• Alle Termine\n• Alle Zuordnungen\n• Alle Anträge\n• Alle Logs\n\nNur Benutzerkonten bleiben erhalten.\n\nDiese Aktion kann nicht rückgängig gemacht werden!')) return;
    setIsPurgingDb(true);
    setPurgeDbMessage('');
    try {
      const response = await syncApi.purgeDatabase(purgeDbPassword);
      setPurgeDbMessage(response.data.message || 'Datenbank geleert');
      setPurgeDbPassword('');
    } catch (err: any) {
      setPurgeDbMessage('Fehler: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsPurgingDb(false);
    }
  };
  
  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <h4 className="text-sm font-medium text-gray-900 mb-3">Graph API Verbindungstest</h4>
      <p className="text-sm text-gray-500 mb-4">
        Testet die Verbindung zum Microsoft Graph API für alle konfigurierten Benutzer.
      </p>
      
      <div className="flex gap-2 flex-wrap">
        <button
          type="button"
          onClick={runTest}
          disabled={isTesting}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-accent-600 hover:bg-accent-700 disabled:opacity-50"
        >
          {isTesting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Teste...
            </>
          ) : (
            <>
              <Activity className="h-4 w-4 mr-2" />
              Verbindung testen
            </>
          )}
        </button>
        
        <button
          type="button"
          onClick={clearAllDelta}
          disabled={isClearing}
          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
        >
          {isClearing ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Lösche...
            </>
          ) : (
            <>
              <Trash2 className="h-4 w-4 mr-2" />
              Delta-Links löschen
            </>
          )}
        </button>
        
        <button
          type="button"
          onClick={purgeAndReset}
          disabled={isPurging}
          className="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
        >
          {isPurging ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Setze zurück...
            </>
          ) : (
            <>
              <AlertTriangle className="h-4 w-4 mr-2" />
              Alles zurücksetzen
            </>
          )}
        </button>
        
        <button
          type="button"
          onClick={purgeDatabase}
          disabled={isPurgingDb}
          className="inline-flex items-center px-4 py-2 border border-red-700 text-sm font-medium rounded-md text-white bg-red-700 hover:bg-red-800 disabled:opacity-50"
        >
          {isPurgingDb ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Lösche...
            </>
          ) : (
            <>
              <Trash2 className="h-4 w-4 mr-2" />
              Datenbank leeren
            </>
          )}
        </button>
      </div>
      
      {clearMessage && (
        <div className="mt-2 text-sm text-amber-700">{clearMessage}</div>
      )}
      
      {purgeMessage && (
        <div className={`mt-2 text-sm ${purgeMessage.includes('Fehler') ? 'text-red-700' : 'text-green-700'}`}>{purgeMessage}</div>
      )}
      
      <div className="mt-4 flex gap-2 items-center">
        <input
          type="password"
          placeholder="Ihr Passwort zur Bestätigung"
          value={purgeDbPassword}
          onChange={(e) => setPurgeDbPassword(e.target.value)}
          className="block w-64 rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm px-3 py-2 border"
        />
      </div>
      
      {purgeDbMessage && (
        <div className={`mt-2 text-sm ${purgeDbMessage.includes('Fehler') ? 'text-red-700' : 'text-green-700'}`}>{purgeDbMessage}</div>
      )}
      
      {testResult && (
        <div className={`mt-4 rounded-md p-3 text-sm ${testResult.success ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
          <p className="font-medium">{testResult.success ? '✅ ' + (testResult.message || 'OK') : '❌ ' + (testResult.message || testResult.error || 'Unbekannter Fehler')}</p>
          {testResult.data?.accounts?.map((acc: any, i: number) => (
            <div key={i} className="mt-2 text-xs">
              <p><strong>Benutzer:</strong> {acc.email}</p>
              <p><strong>Object ID:</strong> {acc.object_id}</p>
              <p><strong>HTTP:</strong> {acc.http_code}</p>
              <p><strong>Kontakte:</strong> {acc.contacts_found}</p>
              {acc.error && <p><strong>Fehler:</strong> {acc.error}</p>}
            </div>
          ))}
          {testResult.data?.delta_links && testResult.data.delta_links.length > 0 && (
            <div className="mt-2 text-xs border-t border-green-200 pt-2">
              <p><strong>Delta-Links:</strong></p>
              {testResult.data.delta_links.map((dl: any, i: number) => (
                <p key={i}>{dl.user_email}: {dl.has_delta_link ? `vorhanden (${dl.delta_link_length} Zeichen)` : 'keiner'}</p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================
// Module-Verwaltung (nur Admin): globaler Kill-Switch + Rollen-Standard
// pro Modul. Per-User-Overrides liegen im Benutzer-Bearbeitungsdialog.
// ============================================
function ModulesPanel() {
  const [modules, setModules] = useState<ModuleConfig[]>([]);
  // Lokale Bearbeitungskopie: {module_key: {is_enabled, role_defaults}}
  const [drafts, setDrafts] = useState<Record<string, { is_enabled: boolean; role_defaults: Record<string, boolean> }>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadModules();
  }, []);

  const loadModules = async () => {
    try {
      const response = await adminApi.getModules();
      const data: ModuleConfig[] = response.data.data;
      setModules(data);
      setDrafts(Object.fromEntries(data.map((m) => [
        m.module_key,
        {
          is_enabled: !!m.is_enabled,
          role_defaults: { user: !!m.role_defaults?.user, approver: !!m.role_defaults?.approver, admin: !!m.role_defaults?.admin },
        },
      ])));
    } catch (err) {
      console.error('Failed to load modules:', err);
      setError('Module konnten nicht geladen werden');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (moduleKey: string) => {
    const draft = drafts[moduleKey];
    if (!draft) return;
    setSavingKey(moduleKey);
    setMessage('');
    setError('');
    try {
      await adminApi.updateModule({
        module_key: moduleKey,
        is_enabled: draft.is_enabled,
        role_defaults: draft.role_defaults,
      });
      setMessage(`Moduleinstellungen für „${MODULE_LABELS[moduleKey] ?? moduleKey}" gespeichert`);
      await loadModules();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Speichern');
    } finally {
      setSavingKey(null);
    }
  };

  const setDraft = (moduleKey: string, patch: Partial<{ is_enabled: boolean; role_defaults: Record<string, boolean> }>) => {
    setDrafts((prev) => ({ ...prev, [moduleKey]: { ...prev[moduleKey], ...patch } }));
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  const roleLabels: Record<string, string> = { user: 'Benutzer', approver: 'Genehmiger', admin: 'Administrator' };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        Module global und pro Rolle aktivieren oder deaktivieren. Individuelle Zugriffe einzelner Benutzer werden im
        Benutzer-Bearbeitungsdialog unter „Module" gesetzt. Ein global deaktiviertes Modul ist für niemanden sichtbar.
      </p>

      {message && <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-700">{message}</div>}
      {error && <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">{error}</div>}

      {modules.map((module) => {
        const draft = drafts[module.module_key];
        if (!draft) return null;
        return (
          <div key={module.module_key} className="card p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-gray-900">{MODULE_LABELS[module.module_key] ?? module.module_key}</h3>
                <p className="text-xs text-gray-400 mt-0.5">Modul-Schlüssel: {module.module_key}</p>
              </div>

              <label className="flex items-center gap-3 cursor-pointer">
                <span className="text-sm text-gray-600">Modul aktiv</span>
                <input
                  type="checkbox"
                  checked={draft.is_enabled}
                  onChange={(e) => setDraft(module.module_key, { is_enabled: e.target.checked })}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
              </label>
            </div>

            <p className="text-xs text-gray-400 mb-4">
              „Modul aktiv" deaktiviert das Modul für ALLE Benutzer (inkl. Administratoren).
            </p>

            <div className="flex flex-wrap gap-6 border-t border-gray-100 pt-4">
              {(Object.keys(roleLabels) as Array<'user' | 'approver' | 'admin'>).map((role) => (
                <label key={role} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={draft.role_defaults[role]}
                    onChange={(e) =>
                      setDraft(module.module_key, { role_defaults: { ...draft.role_defaults, [role]: e.target.checked } })
                    }
                    disabled={!draft.is_enabled}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-600">
                    Standard für {roleLabels[role]}
                  </span>
                </label>
              ))}
            </div>

            <div className="mt-4 pt-4 border-t border-gray-100 flex justify-end">
              <button
                onClick={() => handleSave(module.module_key)}
                disabled={savingKey === module.module_key}
                className="btn btn-primary inline-flex items-center"
              >
                {savingKey === module.module_key ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Speichern
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SettingsPanel() {
  const [settings, setSettings] = useState<{
    shared_calendar_email: string | null;
    import_contacts_email: string | null;
    import_calendar_email: string | null;
    ms_graph_tenant_id: string | null;
    ms_graph_client_id: string | null;
    ms_graph_redirect_uri: string | null;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);
  const [calendarEmail, setCalendarEmail] = useState('');
  const [importContactsEmail, setImportContactsEmail] = useState('');
  const [importCalendarEmail, setImportCalendarEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await adminApi.getSettings();
      const data = response.data.data;
      setSettings(data);
      setCalendarEmail(data.shared_calendar_email || '');
      setImportContactsEmail(data.import_contacts_email || '');
      setImportCalendarEmail(data.import_calendar_email || '');
    } catch (err) {
      console.error('Failed to load settings:', err);
      setError('Einstellungen konnten nicht geladen werden');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');
    setError('');
    setIsSaving(true);
    try {
      await adminApi.updateSettings({
        shared_calendar_email: calendarEmail,
        import_contacts_email: importContactsEmail,
        import_calendar_email: importCalendarEmail
      });
      setMessage('Einstellungen gespeichert');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleRunImport = async () => {
    if (!confirm('Import ausführen?\n\nDies zieht Kontakte und Kalender-Termine direkt aus den konfigurierten Import-Konten in die Datenbank.\n\nBestehende Daten werden aktualisiert, neue werden angelegt.\nDie Änderungen werden NICHT automatisch an andere Outlook-Konten gepusht.')) return;
    setIsImporting(true);
    setImportResult(null);
    setError('');
    try {
      const response = await syncApi.runImport();
      setImportResult(response.data.data);
      setMessage('Import abgeschlossen');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Import fehlgeschlagen');
    } finally {
      setIsImporting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">System-Einstellungen</h3>
      
      {message && <div className="mb-4 bg-green-50 border border-green-200 rounded-md p-3 text-sm text-green-700">{message}</div>}
      {error && <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">{error}</div>}
      
      <form onSubmit={handleSave} className="space-y-6">
        <div className="border border-gray-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Kalender-Synchronisation</h4>
          <p className="text-sm text-gray-500 mb-4">
            Der geteilte Outlook-Kalender, der mit GPS synchronisiert wird.
            Alle genehmigten Termine werden in diesen Kalender geschrieben und von dort gelesen.
            Mitarbeiter können den Kalender in Outlook abonnieren.
          </p>
          
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Geteilter Kalender (Outlook E-Mail)</label>
              <input
                type="email"
                value={calendarEmail}
                onChange={(e) => setCalendarEmail(e.target.value)}
                placeholder="z.B. kalender@gpsbau.de"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">
                E-Mail-Adresse des Outlook-Postfachs, das als geteilter Kalender dient.
              </p>
            </div>
          </div>
        </div>
        
        <div className="border border-gray-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Import-Konten (für Massenimport)</h4>
          <p className="text-sm text-gray-500 mb-4">
            Dedizierte Outlook-Konten, aus denen Kontakte und Kalender-Termine direkt in die Datenbank importiert werden.
            Der Import umgeht das Genehmigungsverfahren. Ein manueller Full-Sync ist nötig, um die Daten an andere Konten zu verteilen.
          </p>
          
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Kontakt-Import Konto (Outlook E-Mail)</label>
              <input
                type="email"
                value={importContactsEmail}
                onChange={(e) => setImportContactsEmail(e.target.value)}
                placeholder="z.B. import@gpsbau.de"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">
                Outlook-Konto, aus dem Kontakte direkt in die App importiert werden.
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Kalender-Import Konto (Outlook E-Mail)</label>
              <input
                type="email"
                value={importCalendarEmail}
                onChange={(e) => setImportCalendarEmail(e.target.value)}
                placeholder="z.B. kalender-import@gpsbau.de"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">
                Outlook-Konto, aus dem Kalender-Termine direkt in die App importiert werden.
              </p>
            </div>
            
            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="submit"
                disabled={isSaving}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Speichern...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Speichern
                  </>
                )}
              </button>
              
              <button
                type="button"
                onClick={handleRunImport}
                disabled={isImporting}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-accent-600 hover:bg-accent-700 disabled:opacity-50"
              >
                {isImporting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Import läuft...
                  </>
                ) : (
                  <>
                    <Activity className="h-4 w-4 mr-2" />
                    Import ausführen
                  </>
                )}
              </button>
            </div>
            
            {importResult && (
              <div className="mt-3 bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-blue-800">
                <p className="font-medium">Import-Ergebnis:</p>
                <p>Kontakte: {importResult.contacts?.created || 0} erstellt, {importResult.contacts?.updated || 0} aktualisiert, {importResult.contacts?.skipped || 0} übersprungen</p>
                <p>Kalender: {importResult.calendar?.created || 0} erstellt, {importResult.calendar?.updated || 0} aktualisiert, {importResult.calendar?.skipped || 0} übersprungen</p>
                {(importResult.contacts?.errors || importResult.calendar?.errors) && (
                  <p className="text-red-600 mt-1">Fehler: {importResult.contacts?.errors || ''} {importResult.calendar?.errors || ''}</p>
                )}
              </div>
            )}
          </div>
        </div>
        
        <div className="border border-gray-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Microsoft 365 Integration</h4>
          <div className="space-y-2 text-sm text-gray-500">
            <p><strong>Tenant ID:</strong> {settings?.ms_graph_tenant_id || 'Nicht konfiguriert'}</p>
            <p><strong>Client ID:</strong> {settings?.ms_graph_client_id || 'Nicht konfiguriert'}</p>
            <p><strong>Redirect URI:</strong> {settings?.ms_graph_redirect_uri || 'Nicht konfiguriert'}</p>
          </div>
          <p className="mt-3 text-xs text-gray-400">
            Diese Werte werden in der api/.env konfiguriert und können hier nicht geändert werden.
          </p>
        </div>
        
        <RejectedContactsSection />
        <GraphTestSection />
      </form>
    </div>
  );
}
